// Staff management hooks
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { queryKeys } from '@/lib/query-client'
import type { Staff } from '@/types/entities'
import { isAssistantLoggedIn } from '@/lib/permissions'

/**
 * Fetch all staff for a specific restaurant
 * Supports both admin users and assistants with appropriate permissions
 */
export function useStaff(restaurantId: string | null) {
  const queryClient = useQueryClient()

  return useQuery({
    queryKey: queryKeys.staff.list(restaurantId || ''),
    staleTime: 5 * 60 * 1000, // 5 minutes - staff don't change often
    gcTime: 10 * 60 * 1000, // 10 minutes cache
    refetchOnWindowFocus: false, // Staff don't need real-time updates
    refetchOnMount: false, // Don't refetch if data is fresh
    queryFn: async (): Promise<Staff[]> => {
      if (!restaurantId) {
        return []
      }

      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {
        // Use assistant RPC function that returns zone_ids (avoids direct staff_zones reads)
        const { data, error} = await (supabase.rpc as any)('assistant_get_staff_with_zones', {
          p_restaurant_id: restaurantId,
        })

        if (error) {
          // If permission denied, return empty array instead of throwing
          if (error.message?.includes('PERMISSION_DENIED')) {
            console.warn('Assistant does not have permission to view staff')
            return []
          }
          throw error
        }

        // Try to enrich with cached zones/specialties if already fetched elsewhere
        const cachedZones = queryClient.getQueryData<any[]>(['zones', 'list', restaurantId]) || []
        const cachedSpecialties =
          queryClient.getQueryData<any[]>(['kitchenSpecialties', 'list', restaurantId]) || []

        const zonesMap = new Map((cachedZones || []).map((z: any) => [z.id, z]))
        const specialtiesMap = new Map((cachedSpecialties || []).map((sp: any) => [sp.id, sp]))

        return (data || []).map((s: any) => {
          const zoneIds: string[] = Array.isArray(s.zone_ids) ? s.zone_ids : []
          const specialty = s.specialty_id ? specialtiesMap.get(s.specialty_id) : null
          const zone = s.zone_id ? zonesMap.get(s.zone_id) : null

          const zones = zoneIds
            .map((zid: string) => zonesMap.get(zid))
            .filter((z: any) => z != null)
            .map((z: any) => ({
              id: z.id,
              restaurantId: restaurantId,
              nameAr: z.nameAr ?? z.name_ar,
              nameFr: z.nameFr ?? z.name_fr,
              isActive: z.isActive ?? z.is_active,
              sortOrder: z.sortOrder ?? z.sort_order ?? 0,
              createdAt: z.createdAt ?? z.created_at ?? '',
              updatedAt: z.updatedAt ?? z.updated_at ?? '',
            }))

          return {
            id: s.id,
            restaurantId: s.restaurant_id,
            username: s.username || '',
            email: s.email,
            role: s.role as 'server' | 'kitchen' | 'printer',
            specialtyId: s.specialty_id || null,
            zoneId: s.zone_id || null,
            zoneIds,
            isActive: s.is_active ?? true,
            receiptHeader: s.receipt_header || null,
            receiptFooter: s.receipt_footer || null,
            deletedAt: s.deleted_at || null,
            createdAt: s.created_at || '',
            updatedAt: s.updated_at || '',
            specialty: specialty
              ? {
                  id: specialty.id,
                  restaurantId: restaurantId,
                  nameAr: specialty.nameAr ?? specialty.name_ar,
                  nameFr: specialty.nameFr ?? specialty.name_fr,
                  color: specialty.color,
                  createdAt: specialty.createdAt ?? specialty.created_at ?? '',
                  updatedAt: specialty.updatedAt ?? specialty.updated_at ?? '',
                }
              : undefined,
            zone: zone
              ? {
                  id: zone.id,
                  restaurantId: restaurantId,
                  nameAr: zone.nameAr ?? zone.name_ar,
                  nameFr: zone.nameFr ?? zone.name_fr,
                  isActive: zone.isActive ?? zone.is_active,
                  sortOrder: zone.sortOrder ?? zone.sort_order ?? 0,
                  createdAt: zone.createdAt ?? zone.created_at ?? '',
                  updatedAt: zone.updatedAt ?? zone.updated_at ?? '',
                }
              : undefined,
            zones: zones.length > 0 ? zones : undefined,
          } as Staff
        })
      }

      // Admin user - use direct query
      const { data, error } = await supabase
        .from('staff')
        .select(`
          *,
          specialty:kitchen_specialties(
            id,
            name_ar,
            name_fr,
            color
          ),
          zone:zones(
            id,
            name_ar,
            name_fr,
            is_active
          ),
          staff_zones(
            zone_id,
            zones(
              id,
              name_ar,
              name_fr,
              is_active
            )
          )
        `)
        .eq('restaurant_id', restaurantId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })

      if (error) {
        throw error
      }

      return ((data || []) as any[]).map((s: any) => {
        const zoneIds = (s.staff_zones || [])
          .map((sz: any) => sz.zone_id)
          .filter((id: string) => id != null)

        const zones = (s.staff_zones || [])
          .map((sz: any) => sz.zones)
          .filter((z: any) => z != null)
          .map((z: any) => ({
            id: z.id,
            restaurantId: restaurantId,
            nameAr: z.name_ar,
            nameFr: z.name_fr,
            isActive: z.is_active,
            sortOrder: 0,
            createdAt: '',
            updatedAt: '',
          }))

        return {
          id: s.id,
          restaurantId: s.restaurant_id,
          username: s.username || '',
          email: s.email,
          role: s.role as 'server' | 'kitchen' | 'printer',
          specialtyId: s.specialty_id || null,
          zoneId: s.zone_id || null, // Keep for backward compatibility
          zoneIds: zoneIds,
          isActive: s.is_active ?? true,
          receiptHeader: s.receipt_header || null,
          receiptFooter: s.receipt_footer || null,
          deletedAt: s.deleted_at || null,
          createdAt: s.created_at || '',
          updatedAt: s.updated_at || '',
          specialty: s.specialty ? {
            id: s.specialty.id,
            restaurantId: restaurantId,
            nameAr: s.specialty.name_ar,
            nameFr: s.specialty.name_fr,
            color: s.specialty.color,
            createdAt: '',
            updatedAt: '',
          } : undefined,
          zone: s.zone ? {
            id: s.zone.id,
            restaurantId: restaurantId,
            nameAr: s.zone.name_ar,
            nameFr: s.zone.name_fr,
            isActive: s.zone.is_active,
            sortOrder: 0,
            createdAt: '',
            updatedAt: '',
          } : undefined,
          zones: zones.length > 0 ? zones : undefined,
        } as Staff
      })
    },
    enabled: !!restaurantId,
  })
}

/**
 * Fetch single staff member by ID
 */
export function useStaffMember(staffId: string) {
  return useQuery({
    queryKey: queryKeys.staff.detail(staffId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes cache
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    queryFn: async (): Promise<Staff> => {
      // Optimized: Use single query with join instead of two sequential queries
      const { data, error } = await supabase
        .from('staff')
        .select(`
          *,
          specialty:kitchen_specialties(
            id,
            name_ar,
            name_fr,
            color
          ),
          zone:zones(
            id,
            name_ar,
            name_fr,
            is_active
          ),
          staff_zones(
            zone_id,
            zones(
              id,
              name_ar,
              name_fr,
              is_active
            )
          )
        `)
        .eq('id', staffId)
        .single()

      if (error) {
        throw error
      }

      const staffData = data as any;

      const zoneIds = (staffData.staff_zones || [])
        .map((sz: any) => sz.zone_id)
        .filter((id: string) => id != null)

      const zones = (staffData.staff_zones || [])
        .map((sz: any) => sz.zones)
        .filter((z: any) => z != null)

      return {
        id: staffData.id,
        restaurantId: staffData.restaurant_id,
        username: staffData.username || '',
        email: staffData.email,
        role: staffData.role as 'server' | 'kitchen' | 'printer',
        specialtyId: staffData.specialty_id || null,
        zoneId: staffData.zone_id || null, // Keep for backward compatibility
        zoneIds: zoneIds,
        isActive: staffData.is_active ?? true,
        receiptHeader: staffData.receipt_header || null,
        receiptFooter: staffData.receipt_footer || null,
        deletedAt: staffData.deleted_at || null,
        createdAt: staffData.created_at || '',
        updatedAt: staffData.updated_at || '',
        specialty: staffData.specialty ? {
          id: staffData.specialty.id,
          restaurantId: staffData.restaurant_id,
          nameAr: staffData.specialty.name_ar,
          nameFr: staffData.specialty.name_fr,
          color: staffData.specialty.color,
          createdAt: '',
          updatedAt: '',
        } : undefined,
        zone: staffData.zone ? {
          id: staffData.zone.id,
          restaurantId: staffData.restaurant_id,
          nameAr: staffData.zone.name_ar,
          nameFr: staffData.zone.name_fr,
          isActive: staffData.zone.is_active,
          sortOrder: 0,
          createdAt: '',
          updatedAt: '',
        } : undefined,
        zones: zones.length > 0 ? zones.map((z: any) => ({
          id: z.id,
          restaurantId: staffData.restaurant_id,
          nameAr: z.name_ar,
          nameFr: z.name_fr,
          isActive: z.is_active,
          sortOrder: 0,
          createdAt: '',
          updatedAt: '',
        })) : undefined,
      } as Staff
    },
    enabled: !!staffId,
  })
}

/**
 * Create new staff member mutation
 * Supports both admin users and assistants with appropriate permissions
 */
export function useCreateStaff() {
  const queryClient = useQueryClient()

  return useMutation({
    retry: false, // Disable automatic retries
    mutationFn: async (request: {
      restaurantId: string
      username: string
      password: string
      role: 'server' | 'kitchen' | 'printer'
      specialtyId?: string | null
      zoneId?: string | null // Deprecated, use zoneIds
      zoneIds?: string[] // Array of zone IDs
      receiptHeader?: string | null // Custom header for receipts (printer role)
      receiptFooter?: string | null // Custom footer for receipts (printer role)
    }) => {
      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {
        // Use assistant RPC function - it supports zone_ids directly
        const zoneIdsToAssign = request.zoneIds || (request.zoneId ? [request.zoneId] : [])

        const createStaffParams: Record<string, any> = {
          p_restaurant_id: request.restaurantId,
          p_username: request.username,
          p_password: request.password,
          p_role: request.role,
        };
        if (request.specialtyId) createStaffParams['p_specialty_id'] = request.specialtyId;
        if (zoneIdsToAssign.length > 0) createStaffParams['p_zone_ids'] = zoneIdsToAssign;
        if (request.receiptHeader) createStaffParams['p_receipt_header'] = request.receiptHeader;
        if (request.receiptFooter) createStaffParams['p_receipt_footer'] = request.receiptFooter;
        const { data, error } = await supabase.rpc('assistant_create_staff', createStaffParams as any)

        if (error) {
          if (error.message?.includes('PERMISSION_DENIED')) {
            throw new Error('Permission denied')
          }
          throw error
        }

        if (!data) {
          throw new Error('Failed to create staff member')
        }

        return data
      }

      // Admin user - use direct query
      // Call the database function
      const params: {
        p_restaurant_id: string;
        p_username: string;
        p_password: string;
        p_role: string;
        p_specialty_id?: string;
      } = {
        p_restaurant_id: request.restaurantId,
        p_username: request.username,
        p_password: request.password,
        p_role: request.role,
      };

      if (request.specialtyId) {
        params.p_specialty_id = request.specialtyId;
      }

      const { data, error } = await supabase.rpc('create_staff', params)

      if (error) {
        throw error
      }

      if (!data) {
        throw new Error('Failed to create staff member')
      }

      const result = data as any
      const staffId = Array.isArray(result) ? result[0]?.id : result?.id

      if (!staffId) {
        throw new Error('Failed to get created staff ID')
      }

      // Handle zone assignments (use zoneIds if provided, otherwise fall back to zoneId for backward compatibility)
      const zoneIdsToAssign = request.zoneIds || (request.zoneId ? [request.zoneId] : [])

      if (zoneIdsToAssign.length > 0) {
        // Insert into staff_zones junction table
        const zoneInserts = zoneIdsToAssign.map((zoneId) => ({
          staff_id: staffId,
          zone_id: zoneId,
        }))

        const { error: zonesError } = await supabase
          .from('staff_zones')
          .insert(zoneInserts)

        if (zonesError) {
          throw zonesError
        }

        // Also update zone_id for backward compatibility (use first zone)
        if (zoneIdsToAssign.length > 0) {
          await supabase
            .from('staff')
            .update({ zone_id: zoneIdsToAssign[0] || null })
            .eq('id', staffId)
        }
      }

      // Update receipt header/footer for printer role
      if (request.role === 'printer' && (request.receiptHeader || request.receiptFooter)) {
        await supabase
          .from('staff')
          .update({
            receipt_header: request.receiptHeader || null,
            receipt_footer: request.receiptFooter || null,
          } as any)
          .eq('id', staffId)
      }

      // create_staff returns staff data
      return data
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.staff.all() })
      queryClient.invalidateQueries({
        queryKey: queryKeys.staff.list(variables.restaurantId),
      })
    },
  })
}

/**
 * Update staff member mutation
 * Supports both admin users and assistants with appropriate permissions
 */
export function useUpdateStaff(staffId: string, restaurantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request: {
      role?: 'server' | 'kitchen' | 'printer'
      isActive?: boolean
      password?: string
      specialtyId?: string | null
      zoneId?: string | null // Deprecated, use zoneIds
      zoneIds?: string[] // Array of zone IDs
      receiptHeader?: string | null // Custom header for receipts (printer role)
      receiptFooter?: string | null // Custom footer for receipts (printer role)
    }) => {
      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {
        // If password is provided, update it via assistant RPC
        if (request.password) {
          const { error: passwordError } = await supabase.rpc('assistant_update_staff_password', {
            p_restaurant_id: restaurantId,
            p_staff_id: staffId,
            p_new_password: request.password,
          })

          if (passwordError) {
            if (passwordError.message?.includes('PERMISSION_DENIED')) {
              throw new Error('Permission denied')
            }
            throw passwordError
          }
        }

        // Build zone_ids for the update call
        let zoneIdsToUpdate: string[] | undefined = undefined
        if (request.zoneIds !== undefined) {
          zoneIdsToUpdate = request.zoneIds
        } else if (request.zoneId !== undefined) {
          zoneIdsToUpdate = request.zoneId ? [request.zoneId] : []
        }

        // Update staff via RPC - all fields are optional (avoid accidentally overwriting role)
        const updateStaffParams: Record<string, any> = {
          p_restaurant_id: restaurantId,
          p_staff_id: staffId,
        }
        if (request.role !== undefined) updateStaffParams['p_role'] = request.role
        if (request.isActive !== undefined) updateStaffParams['p_is_active'] = request.isActive
        if (zoneIdsToUpdate !== undefined) updateStaffParams['p_zone_ids'] = zoneIdsToUpdate
        if (request.specialtyId) updateStaffParams['p_specialty_id'] = request.specialtyId
        if (request.receiptHeader !== undefined) updateStaffParams['p_receipt_header'] = request.receiptHeader
        if (request.receiptFooter !== undefined) updateStaffParams['p_receipt_footer'] = request.receiptFooter
        const { error: updateError } = await supabase.rpc('assistant_update_staff', updateStaffParams as any)

        if (updateError) {
          if (updateError.message?.includes('PERMISSION_DENIED')) {
            throw new Error('Permission denied')
          }
          throw updateError
        }

        return { id: staffId }
      }

      // Admin user - use direct query
      // If password is provided, update it via RPC
      if (request.password) {
        const { data: passwordResult, error: passwordError } = await supabase.rpc(
          'update_staff_password',
          {
            p_staff_id: staffId,
            p_password: request.password,
          }
        )

        if (passwordError) {
          throw passwordError
        }

        if (!passwordResult || passwordResult.length === 0 || !passwordResult[0]?.success) {
          throw new Error(passwordResult?.[0]?.message || 'Failed to update password')
        }
      }

      // Handle zone assignments if zoneIds is provided
      if (request.zoneIds !== undefined) {
        // Delete existing zone assignments
        const { error: deleteError } = await supabase
          .from('staff_zones')
          .delete()
          .eq('staff_id', staffId)

        if (deleteError) {
          throw deleteError
        }

        // Insert new zone assignments
        if (request.zoneIds.length > 0) {
          const zoneInserts = request.zoneIds.map((zoneId) => ({
            staff_id: staffId,
            zone_id: zoneId,
          }))

          const { error: zonesError } = await supabase
            .from('staff_zones')
            .insert(zoneInserts)

          if (zonesError) {
            throw zonesError
          }

          // Update zone_id for backward compatibility (use first zone)
          const { error: updateError } = await supabase
            .from('staff')
            .update({ zone_id: request.zoneIds[0] || null })
            .eq('id', staffId)

          if (updateError) {
            throw updateError
          }
        } else {
          // If no zones, set zone_id to null
          const { error: updateError } = await supabase
            .from('staff')
            .update({ zone_id: null })
            .eq('id', staffId)

          if (updateError) {
            throw updateError
          }
        }
      } else if (request.zoneId !== undefined) {
        // Backward compatibility: handle single zoneId
        // Delete existing zone assignments
        const { error: deleteError } = await supabase
          .from('staff_zones')
          .delete()
          .eq('staff_id', staffId)

        if (deleteError) {
          throw deleteError
        }

        // Insert new zone assignment if provided
        if (request.zoneId) {
          const { error: zonesError } = await supabase
            .from('staff_zones')
            .insert({ staff_id: staffId, zone_id: request.zoneId })

          if (zonesError) {
            throw zonesError
          }

          const { error: updateError } = await supabase
            .from('staff')
            .update({ zone_id: request.zoneId })
            .eq('id', staffId)

          if (updateError) {
            throw updateError
          }
        } else {
          const { error: updateError } = await supabase
            .from('staff')
            .update({ zone_id: null })
            .eq('id', staffId)

          if (updateError) {
            throw updateError
          }
        }
      }

      // Update other fields if any are provided
      const updateData: {
        role?: 'server' | 'kitchen' | 'printer'
        is_active?: boolean | null
        specialty_id?: string | null
        receipt_header?: string | null
        receipt_footer?: string | null
      } = {}

      if (request.role !== undefined) updateData.role = request.role
      if (request.isActive !== undefined) updateData.is_active = request.isActive
      if (request.specialtyId !== undefined) updateData.specialty_id = request.specialtyId
      if (request.receiptHeader !== undefined) updateData.receipt_header = request.receiptHeader
      if (request.receiptFooter !== undefined) updateData.receipt_footer = request.receiptFooter

      // Only update if there are fields to update
      if (Object.keys(updateData).length > 0) {
        const { data, error } = await supabase
          .from('staff')
          .update(updateData as any)
          .eq('id', staffId)
          .select()
          .single()

        if (error) {
          throw error
        }

        return data
      }

      return null
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.staff.all() })
      queryClient.invalidateQueries({
        queryKey: queryKeys.staff.list(restaurantId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.staff.detail(staffId),
      })
    },
  })
}

/**
 * Soft delete staff member mutation
 * Supports both admin users and assistants with appropriate permissions
 */
export function useDeleteStaff(restaurantId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (staffId: string) => {
      // Check if assistant is logged in
      if (isAssistantLoggedIn()) {
        const { error } = await supabase.rpc('assistant_delete_staff', {
          p_restaurant_id: restaurantId,
          p_staff_id: staffId,
        })

        if (error) {
          if (error.message?.includes('PERMISSION_DENIED')) {
            throw new Error('Permission denied')
          }
          throw error
        }
        return
      }

      // Admin user - use direct query
      const { data, error } = await supabase
        .from('staff')
        .update({
          deleted_at: new Date().toISOString(),
          is_active: false,
        })
        .eq('id', staffId)
        .select()

      if (error) {
        throw error
      }

      // Verify that the update actually affected a row
      if (!data || data.length === 0) {
        throw new Error('Failed to delete staff member. No rows were updated.')
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.staff.all() })
      queryClient.invalidateQueries({
        queryKey: queryKeys.staff.list(restaurantId),
      })
    },
  })
}
